import { useSearchParams } from 'react-router-dom';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export interface PrivateAppointmentParams {
  isPrivate: boolean;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  contactId?: string;
}

export interface TestingParams {
  isTesting: boolean;
}

export const useLocationParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { i18n } = useTranslation();

  // Read initial URL parameters
  useEffect(() => {
    const lang = searchParams.get('lang');
    if (lang && ['en', 'fr'].includes(lang)) {
      i18n.changeLanguage(lang);
    }
  }, []); // Only run on mount

  // Get the current location from URL or default to Montreal
  const getLocation = () => {
    const location = searchParams.get('location');
    return location && ['Montreal', 'Toronto'].includes(location) ? location : 'Montreal';
  };

  // Get private appointment parameters from URL
  const getPrivateAppointmentParams = (): PrivateAppointmentParams => {
    const isPrivate = searchParams.get('privateAppointments') === 'true';
    
    if (!isPrivate) {
      return { isPrivate: false };
    }

    return {
      isPrivate: true,
      firstName: searchParams.get('firstName') || undefined,
      lastName: searchParams.get('lastName') || undefined,
      email: searchParams.get('email') || undefined,
      phone: searchParams.get('phone') || undefined,
      contactId: searchParams.get('contactId') || undefined,
    };
  };

  // Get testing parameters from URL
  const getTestingParams = (): TestingParams => {
    return {
      isTesting: searchParams.get('testingAppointments') === 'true',
    };
  };

  // Update URL parameters while preserving other params
  const updateParams = (updates: { location?: string; lang?: string }) => {
    const newParams = new URLSearchParams(searchParams);
    
    if (updates.location) {
      newParams.set('location', updates.location);
    }
    if (updates.lang) {
      newParams.set('lang', updates.lang);
    }
    
    setSearchParams(newParams);
  };

  return {
    location: getLocation(),
    privateAppointmentParams: getPrivateAppointmentParams(),
    testingParams: getTestingParams(),
    updateParams
  };
};
